import { useState, type FC } from "react";
import { useTranslation } from "@privacy/translations";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import type { Agreement, GetPartnerByIdResponse, PartnerRole } from "../../api/partners";
import {
  partnerByIdQueryOptions,
  deleteAgreementMutationOptions,
  deletePartnerRoleContactPersonMutationOptions,
  updatePartnerRoleMutationOptions,
} from "../../api/partners";
import { DynamicGrid } from "../../dynamicGrid";
import { Drawer } from "../../shared/Drawer";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { PartnerDrawerHeader } from "./PartnerDrawerHeader";
import { ROLES_INFO } from "./constants";
import {
  CONNECTED_ENTITIES_DRAWER_PATH_SUFFIX,
  CONNECTED_ENTITIES_FORM_TYPES_INFO,
} from "./PartnerRoleSubDrawerConnectedEntities";
import { useForm } from "react-hook-form";
import { DynamicForm } from "../../dynamicForm";
import { AddDialogTrigger } from "../../dynamicForm/shared/AddDialogTrigger";
import { AddOrUpdateContactPersonDialog, ContactPersonInput, type ContactPerson } from "../../shared/ContactPerson";
import { RoleCardStatusBadge } from "./PartnerDrawerRolesSection";
import { CreateAndUpdateAgreementFormDialog } from "../../agreements/CreateOrUpdateAgreementFormDialog";
import { AgreementUsageNote } from "./AgreementUsageNote";
import { ConfirmDeleteDialog } from "../../shared/ConfirmDeleteDialog";
import { FileTextIcon, PencilIcon, XIcon } from "../../shared/icons";
import { DynamicShared } from "../../dynamicShared";
import type { Theme } from "@mui/material";
import { AgreementDate } from "../../agreements/AgreementDate";
import { useMutation, useQuery } from "@tanstack/react-query";

type PartnerRoleSubdrawerProps = { role: PartnerRole };

export const PartnerRoleSubdrawer: FC<PartnerRoleSubdrawerProps> = ({ role }) => {
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const partnerId = DynamicGrid.useSelectedRowId();
  const partnerByIdQuery = useQuery(partnerByIdQueryOptions(partnerId));

  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!partnerByIdQuery.data) return <Drawer.Loading />;

        const partner = partnerByIdQuery.data;
        const roleData = partner.rolesAndRelationships.find(r => r.role === role);

        if (!roleData) {
          throw new Error(`No role found for partner ${partnerId} and role ${role}`);
        }

        const { Icon1 } = CONNECTED_ENTITIES_FORM_TYPES_INFO[roleData.formType];

        return (
          <Stack sx={{ height: "100%" }}>
            <PartnerDrawerHeader status="hidden" partner={partner} onClose={() => navigate(paths.partnersGrid())} />
            <Drawer.SubDrawerHeader
              onBackClick={() => navigate(paths.partnersInline(partner.id))}
              title={
                <Stack
                  direction="row"
                  sx={{ width: "100%", gap: 2, justifyContent: "space-between", alignItems: "center" }}
                >
                  <Typography variant="h3" sx={{ fontWeight: "600" }}>
                    {t(ROLES_INFO[role].labelKey)}
                  </Typography>
                  <RoleCardStatusBadge activeStatus={roleData.activeStatus} role={role} formType={roleData.formType} />
                </Stack>
              }
              renderIcon={props =>
                ROLES_INFO[role].renderIcon({ ...props, size: "md", activeStatus: roleData.activeStatus })
              }
            />
            <Drawer.Content sx={{ py: 0 }}>
              <PartnerRoleDescriptionField
                partnerId={partner.id}
                role={role}
                description={roleData.description}
                partnerName={partner.name}
              />
              <PartnerRoleAgreementField partnerId={partner.id} roleData={roleData} />
              <PartnerRoleContactPersonField
                partnerId={partner.id}
                role={role}
                contactPerson={roleData.contactPerson}
              />
              <Drawer.GoToSubDrawerButtonsWrapper>
                <Drawer.GoToSubDrawerButton
                  Icon={Icon1}
                  count={roleData.totalConnectedEntitiesCount}
                  label={t(CONNECTED_ENTITIES_FORM_TYPES_INFO[roleData.formType].subDrawerButtonLabelKey)}
                  onClick={() => navigate(CONNECTED_ENTITIES_DRAWER_PATH_SUFFIX)}
                />
              </Drawer.GoToSubDrawerButtonsWrapper>
            </Drawer.Content>
          </Stack>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};

type PartnerRoleDescriptionFieldProps = {
  partnerId: string;
  role: PartnerRole;
  description?: string | null;
  partnerName: string;
};

const DESCRIPTION_FIELD_ID = "description";
const PartnerRoleDescriptionField: FC<PartnerRoleDescriptionFieldProps> = ({
  partnerId,
  role,
  description,
  partnerName,
}) => {
  const { t } = useTranslation();
  const updateRoleMutation = useMutation(updatePartnerRoleMutationOptions({ partnerId, role }));
  const form = useForm<{ description: string }>({
    defaultValues: { [DESCRIPTION_FIELD_ID]: description ?? t(ROLES_INFO[role].descriptionKey, { partnerName }) },
  });

  return (
    <DynamicForm.InlineFieldPrimary
      htmlFor={DESCRIPTION_FIELD_ID}
      label={t("Partner.Roles.Description")}
      input={
        <DynamicForm.StyledInlineTextInput
          id={DESCRIPTION_FIELD_ID}
          form={form}
          numberOfRows={4}
          onSave={value =>
            updateRoleMutation.mutateAsync(
              { description: value },
              { onSuccess: () => form.reset({ [DESCRIPTION_FIELD_ID]: value }) }
            )
          }
          renderErrorMessage={({ isSaveError }) => {
            if (isSaveError && updateRoleMutation.isError) return <DynamicForm.ErrorMessage />;
          }}
        />
      }
    />
  );
};

type PartnerRoleContactPersonFieldProps = {
  partnerId: string;
  role: PartnerRole;
  contactPerson?: ContactPerson | null;
};

const PartnerRoleContactPersonField: FC<PartnerRoleContactPersonFieldProps> = ({ partnerId, role, contactPerson }) => {
  const { t } = useTranslation();
  const updateRoleMutation = useMutation(updatePartnerRoleMutationOptions({ partnerId, role }));
  const deleteRoleContactPersonMutation = useMutation(
    deletePartnerRoleContactPersonMutationOptions({ partnerId, role })
  );

  const handleUpdateContactPerson = (contactPerson: ContactPerson) => updateRoleMutation.mutateAsync({ contactPerson });

  return (
    <DynamicForm.InlineFieldPrimary
      label={t("Partner.Roles.Contact.Label")}
      input={
        <>
          {contactPerson ? (
            <ContactPersonInput
              contactPerson={contactPerson}
              onEdit={handleUpdateContactPerson}
              onDelete={deleteRoleContactPersonMutation.mutateAsync}
            />
          ) : (
            <AddDialogTrigger
              isEmpty
              renderDialog={props => <AddOrUpdateContactPersonDialog {...props} onSubmit={handleUpdateContactPerson} />}
            />
          )}
        </>
      }
    />
  );
};

type PartnerRoleAgreementFieldProps = {
  partnerId: string;
  roleData: GetPartnerByIdResponse["rolesAndRelationships"][number];
};

const ACTION_BUTTONS_CLASS_NAME = "action-buttons";

const PartnerRoleAgreementField: FC<PartnerRoleAgreementFieldProps> = ({ partnerId, roleData }) => {
  const { t } = useTranslation();

  return (
    <DynamicForm.InlineFieldPrimary
      label={t("Partner.Roles.Agreement.Label")}
      input={
        <Stack sx={{ gap: 0 }}>
          {roleData.agreementDocuments.map(agreement => (
            <AgreementInput agreement={agreement} partnerId={partnerId} key={agreement.id} />
          ))}

          <AddDialogTrigger
            isEmpty={roleData.agreementDocuments.length === 0}
            renderDialog={props => (
              <CreateAndUpdateAgreementFormDialog
                {...props}
                partnerId={partnerId}
                defaultValues={{ type: roleData.agreementType }}
              />
            )}
          >
            {t("Partner.Agreement.AddButton")}
          </AddDialogTrigger>
        </Stack>
      }
    />
  );
};

type AgreementInputProps = { partnerId: string; agreement: Agreement };

const AgreementInput: FC<AgreementInputProps> = ({ partnerId, agreement }) => {
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = useState<"edit" | "delete" | null>(null);
  const deleteAgreementMutation = useMutation(deleteAgreementMutationOptions({ partnerId }));
  return (
    <>
      {openDialog === "edit" && (
        <CreateAndUpdateAgreementFormDialog
          onClose={() => setOpenDialog(null)}
          partnerId={partnerId}
          agreementId={agreement.id}
          defaultValues={{
            title: agreement.title,
            url: agreement.url,
            type: agreement.type,
            validUntil: agreement.validUntil,
          }}
        />
      )}
      {openDialog === "delete" && (
        <ConfirmDeleteDialog
          isOpen
          onClose={() => setOpenDialog(null)}
          onDelete={() => deleteAgreementMutation.mutateAsync(agreement.id)}
          headerText={t("Partner.Agreement.Delete.ConfirmHeader")}
          message={
            <>
              {t("Partner.Agreement.Delete.ConfirmMessage")}
              <AgreementUsageNote
                count={agreement.selectedOnSystemIds?.length ?? 0}
                oneKey="Partner.Agreement.Delete.SelectedOnSystems.One"
                manyKey="Partner.Agreement.Delete.SelectedOnSystems.Many"
              />
            </>
          }
          confirmButtonLabel={t("Partner.Agreement.Delete.ConfirmButton")}
        />
      )}
      <Box
        sx={{
          "py": 0.5,
          "px": 2,
          "display": "flex",
          "alignItems": "center",
          "gap": 0.5,
          "justifyContent": "space-between",
          "&:hover": { backgroundColor: theme => theme.palette.action.hover },
          [`& .${ACTION_BUTTONS_CLASS_NAME}`]: { opacity: 0 },
          [`&:is(:hover, :focus-within) .${ACTION_BUTTONS_CLASS_NAME}`]: { opacity: 1 },
          "borderRadius": 1,
        }}
      >
        <AgreementContent agreement={agreement} />
        <Stack direction="row" className={ACTION_BUTTONS_CLASS_NAME} sx={{ gap: 0.5 }}>
          <Tooltip title={t("Partner.Agreement.Grid.Edit.Tooltip")}>
            <IconButton
              aria-label={t("Partner.Agreement.Grid.Edit.Tooltip")}
              size="small"
              onClick={() => setOpenDialog("edit")}
              sx={{ "&:hover": { backgroundColor: theme => theme.palette.grey["300"] } }}
            >
              <PencilIcon size={14} color={(theme: Theme) => theme.palette.text.secondary} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("Partner.Agreement.Grid.Delete.Tooltip")}>
            <IconButton
              aria-label={t("Partner.Agreement.Grid.Delete.Tooltip")}
              size="small"
              onClick={() => setOpenDialog("delete")}
              sx={{ "&:hover": { backgroundColor: theme => theme.palette.grey["300"] } }}
            >
              <XIcon size={14} color={(theme: Theme) => theme.palette.text.secondary} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </>
  );
};

type AgreementContentProps = { agreement: Agreement };

const AgreementContent: FC<AgreementContentProps> = ({ agreement }) => (
  <DynamicShared.ExternalLink Icon={FileTextIcon} href={agreement.url}>
    <Typography component="span" sx={{ fontSize: theme => theme.typography.pxToRem(14), fontWeight: "600" }}>
      {agreement.title}
      <AgreementDate agreement={agreement} />
    </Typography>
  </DynamicShared.ExternalLink>
);
